import React, { useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import { toast } from "react-toastify";
import { PhoneInput } from "react-international-phone";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { eventBookingActions } from "../../../../redux/actions";

import { ReactComponent as BackIcon } from "../../../../assets/icons/back.svg";
import { ReactComponent as PlusIcon } from "../../../../assets/icons/plus-square.svg";

import {
  messages,
  EMPTY_STRING,
  DEFAULT_COUNTRY,
  QUESTION_TYPE_IDS,
  eventDetailsFields,
  configurationsConstants,
} from "../../../../constants";
import {
  customToast,
  isPhoneValid,
  isArrayWithItems,
  isEventDetailsValid,
} from "../../../../shared";
import { EVENT_PHASES } from "../Tabs/EventDetails";

const { PHONE_NUMBER, CONTACT_NAME, IS_OPTIONAL_DETAILS } = eventDetailsFields;

const EventOnsiteForm = (props) => {
  const { selectedEventId, currentEventType, setCurrentPhase } = props;

  const { t } = useTranslation("common");

  const [phoneError, setPhoneError] = useState(null);

  const handleSubmit = (event, errors, values) => {
    const phoneValue = currentEventType?.phoneNumber;

    if (!phoneValue) {
      setPhoneError(t("please_enter_a_onsite_contact_number"));
      return;
    }

    //stop navigation
    if (errors.length > 0) {
      return;
    }

    const { isPhoneNumberValid } = isPhoneValid(phoneValue);

    if (!isPhoneNumberValid) {
      setPhoneError(messages.PLEASE_ENTER_VALID_PHONE_NUMBER);
      customToast(messages.PLEASE_ENTER_VALID_PHONE_NUMBER, toast.TYPE.ERROR);
      return;
    }

    handleNextEventClick();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const questionTypeId = Number(name);
    if (!isNaN(questionTypeId)) {
      const existingAnswers = currentEventType?.eventQuestionAnswers || [];

      // Find the existing eventQuestionAnswers against the questionTypeId
      const existingAnswer = existingAnswers?.find(
        (q) => q.questionTypeId === questionTypeId,
      );

      // Merge if exists, otherwise create a new one
      const updatedAnswer = existingAnswer
        ? { ...existingAnswer, answer: value }
        : { questionTypeId, answer: value };

      props.onChange(
        selectedEventId,
        eventDetailsFields.EVENT_QUESTION_ANSWERS,
        updatedAnswer,
      );
    } else {
      props.onChange(selectedEventId, name, value);
    }
  };

  const handlePhoneInput = (value) => {
    const { isPhoneNumberValid, phoneValue } = isPhoneValid(value);

    if (!isPhoneNumberValid) {
      setPhoneError(messages.PLEASE_ENTER_VALID_PHONE_NUMBER);
    } else {
      setPhoneError(null);
    }

    props.onChange(selectedEventId, PHONE_NUMBER, phoneValue);
  };

  const currentIndex = props.formState?.findIndex(
    (e) => e.id === props.selectedEventId,
  );

  const isLastEvent = currentIndex === props.formState?.length - 1;

  const handleNextEventClick = () => {
    if (isLastEvent) {
      const firstInvalidEvent = props.formState.find((event) => {
        const eventType = event[eventDetailsFields.EVENT_TYPE] || {};
        const { isPhoneNumberValid } = isPhoneValid(
          event[eventDetailsFields.PHONE_NUMBER],
        );

        if (!isEventDetailsValid(eventType)) {
          event.invalidReason = eventDetailsFields.EVENT_TYPE;
          return true;
        }

        if (!isPhoneNumberValid) {
          event.invalidReason = eventDetailsFields.IS_OPTIONAL_DETAILS;
          return true;
        }

        return false;
      });

      if (firstInvalidEvent) {
        customToast(messages.SOME_FIELDS_IS_MISSING, toast.TYPE.ERROR);

        props.setSelectedEventId(firstInvalidEvent.id);
        props.setEventName(firstInvalidEvent.eventName);

        return;
      }

      props.setEventName(EMPTY_STRING);
      props.onNext();
    } else {
      const nextEvent = getNextEvent(currentIndex);

      if (!nextEvent) {
        customToast(messages.EVENT_SHOT_LIST_IS_EMPTY, toast.TYPE.ERROR);
        return;
      }

      const hasAI = isEventDetailsValid(nextEvent?.eventType);

      props.setSelectedEventId(nextEvent.id);
      props.setEventName(nextEvent.eventName);

      setCurrentPhase(
        hasAI ? EVENT_PHASES.CONTACT_INFO : EVENT_PHASES.AI_RESPONSE,
      );
    }
  };

  const getNextEvent = (startIndex) => {
    for (let i = startIndex + 1; i < props.formState.length; i++) {
      return props.formState[i];
    }
    return null;
  };

  return (
    <div className="event-onsite-information px-3">
      <AvForm
        id="event-onsite-form"
        onSubmit={handleSubmit}
        autoComplete="off"
        noValidate
      >
        <div className="row">
          <div className="col-md-6">
            <label className="label-content-600 line-height-100">
              {t("onsite_contact_name")}:
            </label>
            <AvField
              name={CONTACT_NAME}
              type="text"
              className="form-control"
              value={currentEventType?.contactName}
              onChange={handleChange}
              maxLength="70"
              validate={{
                required: {
                  value: true,
                  errorMessage: t("please_enter_a_onsite_contact_name"),
                },
              }}
            />
          </div>

          <div className="col-md-6">
            <label className="label-content-600 line-height-100">
              {t("onsite_contact_number")}:
            </label>
            <PhoneInput
              name={PHONE_NUMBER}
              defaultCountry={DEFAULT_COUNTRY}
              value={currentEventType[PHONE_NUMBER] || EMPTY_STRING}
              onChange={(e) => handlePhoneInput(e)}
              className={phoneError ? "is-invalid-phone" : EMPTY_STRING}
            />
            {phoneError && (
              <div className="invalid-field-error">{phoneError}</div>
            )}
          </div>

          {(isArrayWithItems(currentEventType?.eventQuestionAnswers) ||
            currentEventType?.isOptionalDetails) && (
            <>
              <div className="col-lg-6 mt-3">
                <h4 className="question-title label-content-600">
                  anything else we should know about your event?
                </h4>
                <p className="question-description text-content mb-2">
                  share a short overview of what your event is and why you’re
                  hosting it — this helps us understand the vibe, goals, and
                  story you’re looking for us to create. feel free to pull
                  language from existing materials like your event page or
                  invite.
                </p>
                <textarea
                  name={QUESTION_TYPE_IDS.CONTENT_USAGE.toString()}
                  id="content-usage"
                  maxLength={configurationsConstants.TEXTAREA_INPUT_LENGTH}
                  cols="30"
                  rows="3"
                  value={
                    (currentEventType?.eventQuestionAnswers || []).find(
                      (q) =>
                        q.questionTypeId === QUESTION_TYPE_IDS.CONTENT_USAGE,
                    )?.answer || EMPTY_STRING
                  }
                  onChange={handleChange}
                ></textarea>
              </div>

              <div className="col-lg-6 mt-3">
                <h4 className="question-title label-content-600">
                  brand guidelines and legal requirements:
                </h4>
                <p className="question-description text-content mb-2">
                  if you are a company and there are specific rules around your
                  brand for example how the brand shows up, legal watch outs,
                  etc.
                </p>
                <textarea
                  name={QUESTION_TYPE_IDS.BRAND_GUIDELINES.toString()}
                  id="question2"
                  maxLength={configurationsConstants.TEXTAREA_INPUT_LENGTH}
                  cols="30"
                  rows="3"
                  value={
                    (currentEventType?.eventQuestionAnswers || []).find(
                      (q) =>
                        q.questionTypeId === QUESTION_TYPE_IDS.BRAND_GUIDELINES,
                    )?.answer || EMPTY_STRING
                  }
                  onChange={handleChange}
                ></textarea>
              </div>
            </>
          )}
        </div>

        {!isArrayWithItems(currentEventType?.eventQuestionAnswers) &&
          !currentEventType?.isOptionalDetails && (
            <div className="add-new-event mt-4">
              <button
                type="button"
                onClick={() =>
                  props.onChange(selectedEventId, IS_OPTIONAL_DETAILS, true)
                }
              >
                <span className="add-event-icon">
                  <PlusIcon />
                </span>
                <span className="add-event-title">
                  {t("add_optional_details")}
                </span>
              </button>
            </div>
          )}

        <div className="d-flex justify-content-center sticky-button-row mt-4 mb-3 px-3">
          <button
            type="button"
            className="event-back-btn button-common button-outline btn-text-grey width-50"
            onClick={() => props.onPrevious()}
          >
            <BackIcon />
            <span className="pl-1">{t("go_back")}</span>
          </button>
          <button
            type="submit"
            className="button-common button-fill width-50 ml-3"
          >
            {isLastEvent ? t("continue") : t("next")}
          </button>
        </div>
      </AvForm>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  selectedEventId: state.eventBooking.selectedEventId,
  currentEventType: state.eventBooking.currentEventType,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setEventName: (data) => dispatch(eventBookingActions.setEventName(data)),
  setSelectedEventId: (data) =>
    dispatch(eventBookingActions.setSelectedEventId(data)),
  checkOut: (data) => dispatch(eventBookingActions.checkOut(data)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(EventOnsiteForm);
